import React from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import { formatQtyForDisplay, parseDecimalInput } from '../utils/decimalInput';

const apiUrl = process.env.REACT_APP_API_URL ?? '';
const QUANTITY_TOLERANCE = 0.000001;

export interface IssueSerial {
  serial: string;
  quantity: number;
  deadline: string;
}

export interface IssueSerialSelection extends IssueSerial {
  id: string;
}

interface UseIssueSerialsOptions {
  isOpen: boolean;
  itemId?: string;
  partnerId?: string;
  requiredQuantity: string | number;
  currentSerial?: string;
  currentDeadline?: string;
}

const isValidDate = (year: number, month: number, day: number) => {
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
};

const deadlineAtEnd = (deadline: string): number | null => {
  const dayMatch = deadline.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dayMatch) {
    const [, day, month, year] = dayMatch.map(Number);
    return isValidDate(year, month, day)
      ? new Date(year, month - 1, day, 23, 59, 59, 999).getTime()
      : null;
  }

  const monthMatch = deadline.match(/^(\d{1,2})[./](\d{4})$/);
  if (!monthMatch) return null;
  const [, month, year] = monthMatch.map(Number);
  if (!isValidDate(year, month, 1)) return null;
  return new Date(year, month, 0, 23, 59, 59, 999).getTime();
};

export const isExpiredIssueSerial = (deadline: string): boolean => {
  const deadlineAt = deadlineAtEnd(deadline);
  return deadlineAt !== null && deadlineAt < Date.now();
};

export const createIssuedRows = <T extends Record<string, any>>(
  row: T,
  serials: IssueSerialSelection[],
  reprice: (row: T, quantity: string) => T,
): T[] => serials.map(({ serial, deadline, quantity }) => reprice(
  { ...row, serial, deadline },
  formatQtyForDisplay(quantity, { showZero: true }),
));

export const useIssueSerials = ({
  isOpen,
  itemId,
  partnerId,
  requiredQuantity,
  currentSerial,
  currentDeadline,
}: UseIssueSerialsOptions) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [stock, setStock] = React.useState<IssueSerial[]>([]);
  const [selectedSerials, setSelectedSerials] = React.useState<IssueSerialSelection[]>([]);
  const [search, setSearch] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const required = parseDecimalInput(requiredQuantity) ?? 0;

  React.useEffect(() => {
    if (!isOpen || !itemId || !partnerId) return;
    let active = true;

    const load = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${apiUrl}/api/stock-overview`, {
          headers: { 'x-auth-token': localStorage.getItem('token') },
          params: { itemId, partners: partnerId },
        });
        if (!active) return;

        const serials = (response.data.stockData ?? [])
          .filter((entry: any) => entry.serial && Number(entry.totalQuantity) > 0)
          .map((entry: any) => ({
            serial: String(entry.serial),
            quantity: Number(entry.totalQuantity),
            deadline: entry.deadline || '',
          }))
          .sort((a: IssueSerial, b: IssueSerial) => (
            (deadlineAtEnd(a.deadline) ?? Infinity) - (deadlineAtEnd(b.deadline) ?? Infinity)
            || a.serial.localeCompare(b.serial)
          ));
        setStock(serials);
      } catch (error: any) {
        if (active) {
          setStock([]);
          showToast(error.response?.data?.message || t('issueSerials.loadError'), 'error');
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => { active = false; };
  }, [isOpen, itemId, partnerId, showToast, t]);

  React.useEffect(() => {
    if (!isOpen) return;
    setSearch('');
    setSelectedSerials(
      currentSerial && !currentSerial.includes(',')
        ? [{ id: currentSerial, serial: currentSerial, quantity: required, deadline: currentDeadline || '' }]
        : [],
    );
  }, [isOpen, currentSerial, currentDeadline, required]);

  React.useEffect(() => {
    if (!stock.length) return;
    setSelectedSerials((current) => current.flatMap((entry) => {
      const available = stock.find((serial) => serial.serial === entry.serial);
      if (!available || isExpiredIssueSerial(available.deadline)) return [];
      return [{
        ...entry,
        deadline: available.deadline,
        quantity: Math.min(entry.quantity, available.quantity),
      }];
    }));
  }, [stock]);

  const selectedQuantity = React.useMemo(
    () => selectedSerials.reduce((sum, entry) => sum + entry.quantity, 0),
    [selectedSerials],
  );
  const difference = required - selectedQuantity;
  const isSelectionValid = selectedSerials.length > 0 && selectedSerials.every((entry) => {
    const available = stock.find((serial) => serial.serial === entry.serial);
    return Number.isFinite(entry.quantity)
      && entry.quantity > 0
      && !!available
      && entry.quantity <= available.quantity + QUANTITY_TOLERANCE
      && !isExpiredIssueSerial(available.deadline);
  });
  const canConfirm = !loading
    && isSelectionValid
    && Math.abs(difference) <= QUANTITY_TOLERANCE;

  const addSerial = (serial: IssueSerial) => {
    if (isExpiredIssueSerial(serial.deadline) || selectedSerials.some((entry) => entry.serial === serial.serial)) return;
    const quantity = Math.min(serial.quantity, Math.max(difference, 0));
    if (quantity > 0) {
      setSelectedSerials((current) => [...current, {
        ...serial,
        id: serial.serial,
        quantity,
      }]);
      return;
    }
    // Nothing left to cover (e.g. the row's current serial is preselected for the full quantity):
    // double-click means "take this serial instead", never a silent no-op.
    setSelectedSerials([{
      ...serial,
      id: serial.serial,
      quantity: Math.min(serial.quantity, required),
    }]);
  };

  const updateQuantity = (id: string, value: string): number | null => {
    const quantity = parseDecimalInput(value);
    const selected = selectedSerials.find((entry) => entry.id === id);
    const available = stock.find((serial) => serial.serial === selected?.serial);
    if (quantity === null || !Number.isFinite(quantity) || quantity <= 0 || !available) return null;
    const accepted = Math.min(quantity, available.quantity);
    setSelectedSerials((current) => current.map((entry) => (
      entry.id === id ? { ...entry, quantity: accepted } : entry
    )));
    return accepted;
  };

  return {
    availableSerials: stock.filter((entry) => (
      entry.serial.toLocaleLowerCase().includes(search.toLocaleLowerCase())
    )),
    selectedSerials,
    search,
    setSearch,
    loading,
    required,
    selectedQuantity,
    difference,
    canConfirm,
    addSerial,
    updateQuantity,
    removeSelected: (id: string) => setSelectedSerials((current) => (
      current.filter((entry) => entry.id !== id)
    )),
    clearSelected: () => setSelectedSerials([]),
  };
};

export default useIssueSerials;
